/* eslint-disable no-prototype-builtins */

import React, { PureComponent } from 'react';
import _ from 'lodash';
import * as PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import Sidebar from '../../components/Sidebar';
import DailyProgram from '../../components/DailyProgram';
import Loader from '../../components/Loader';
import Core from '../../utils/coreEPG';
import { isFullscreen } from '../../utils/isFullscreen';
import epgActions from '../../epgActions';

import {ReactComponent as ChannelsIcon} from 'desktop/assets/icons/channels.svg';

import {
  activeProgramsSelector,
  currentChannelProgramsListSelector,
  currentChannelProgramsMapSelector,
  currentProgramSelector,
  isExtendedProgramListSelector,
  playingProgramSelector,
} from 'desktop/selectors/programs';
import {
  channelsByGenresSelector,
  channelsMapSelector,
  currentChannelAbsoluteIdSelector,
  currentChannelSelector,
  subscriptionInfoMapSelector,
} from 'desktop/selectors/channels';

import {
  getAllChannelsUrl,
  getShowAllChannelsButton
} from 'desktop/selectors/app';

import {
  ChannelsButtonWrap,
  ChannelsSideButton,
  EpgStyled,
  ProgramButton,
  ProgramsButtonWrap,
  ProgramsSideButton,
  StyledArrowIcon,
  StyledIcon,
  StyledText,
} from './styles/AppStyled';

class App extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      programVisible: false,
      fullscreen: false,
      buttonExpanded: false,
      hovered: false,
      isCoreInited: false,
      visibleFullScreenControls: true,
    };
    this.epgCore = new Core(props);
    this.setFullscreenListeners();
    this.playerContainer = document.getElementById('playerContainer');
    this.expanded = false;
    this.smallPlayerTarget = null;
    this.programRendered = false;
  }

  componentDidMount() {
    this.blockSelect(false);
    const bindDesktopApp = (epg) => {
      this.epgCore.init(epg);
      if (window.location.hash.indexOf('#expanded') !== -1) {
        this.toggleExpand(true);
        this.setState({
          programVisible: true,
        });
      }
      this.setState({
        isCoreInited: true,
      });
      this.epgCore.setSkinVisibilityCallBack(this.visibleFullScreenControls);
    };

    window.MggpEPG.onInit(bindDesktopApp);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.epgCore.setProps(nextProps);
  }

  componentDidUpdate(prevProps, prevState) {
    const { programVisible } = this.state;

    if (programVisible && !prevState.programVisible && !this.programRendered) {
      this.programRendered = true;
    }
  }

  componentWillUnmount() {
    this.removeFullscreenListeners();
    this.blockSelect(true);
  }

  blockSelect = (state) => {
    document.onselectstart = () => state; // block selection event to resolve cursor issues in safari
  };

  onFullscreen = () => {
    const fullscreen = isFullscreen();
    const { fullscreen: oldFullscreen } = this.state;

    const epgParent = document.getElementById('epgModule');
    const programButton = document.getElementById('ProgramButton');
    const mainContainer = document.getElementById('MainContainer');
    if (fullscreen) {
      if (programButton.parentNode !== this.playerContainer) {
        this.playerContainer.appendChild(programButton);
      }
      if (mainContainer.parentNode !== this.playerContainer) {
        this.playerContainer.appendChild(mainContainer);
      }
      this.setState({ fullscreen: true });
    } else {
      if (programButton.parentNode !== epgParent) {
        epgParent.appendChild(programButton);
      }
      if (mainContainer.parentNode !== epgParent) {
        epgParent.appendChild(mainContainer);
      }
      this.setState({ fullscreen: false });
    }
    if (fullscreen !== oldFullscreen && this.expanded) {
      this.toggleExpand();
    }
  };

  onPlayerClick = (event) => {
    const el = event.target;
    const parent = el.parentNode;
    let role;

    if(el.getAttribute('data-role')) {
      role = el.getAttribute('data-role');
    } else {
      role = parent.getAttribute('data-role') || parent.getAttribute('type');
    }

    let target;
    switch (role) {
      case 'volume':
        // noop
        break;

      case 'fullscreen':
        // noop
        break;
      default:
        target = parent;
        while (target !== event.currentTarget) {
          if (target.getAttribute('data-role') === 'volume') {
            return;
          }
          target = target.parentNode;
        }
        if (this.expanded) {
          event.stopPropagation();
          this.toggleExpand();
        }
    }
  };

  onMouseEnter = (event) => {
    if (!this.expanded) {
      this.setState({ hovered: true });
    }
  };

  onMouseLeave = () => {
    const { hovered } = this.state;

    if (hovered) {
      this.setState({ hovered: false });
    }
  };

  onTransitionEnd = (event) => {
    const { fullscreen } = this.state;

    if (event.target === event.currentTarget && event.propertyName === 'transform') {
      this.setState({
        programVisible: this.expanded,
      });
      if (fullscreen && this.expanded) {
        this.epgCore.emitAnimationEnd();
      }
    }
  };

  setFullscreenListeners = () => {
    if (document.addEventListener) {
      document.addEventListener('webkitfullscreenchange', this.onFullscreen, false);
      document.addEventListener('mozfullscreenchange', this.onFullscreen, false);
      document.addEventListener('fullscreenchange', this.onFullscreen, false);
      document.addEventListener('MSFullscreenChange', this.onFullscreen, false);
    }
  };

  removeFullscreenListeners = () => {
    if (document.addEventListener) {
      document.removeEventListener('webkitfullscreenchange', this.onFullscreen, false);
      document.removeEventListener('mozfullscreenchange', this.onFullscreen, false);
      document.removeEventListener('fullscreenchange', this.onFullscreen, false);
      document.removeEventListener('MSFullscreenChange', this.onFullscreen, false);
    }
  };

  toggleExpand = () => {
    const { fullscreen, buttonExpanded } = this.state;

    const newExpanded = !this.expanded;
    const playerWrapper = document.getElementById('mggp-player-wrapper');
    if (playerWrapper) {
      if (newExpanded) {
        playerWrapper.addEventListener('click', this.onPlayerClick, true);
      } else {
        playerWrapper.removeEventListener('click', this.onPlayerClick, true);
      }
    }
    this.expanded = newExpanded;
    this.epgCore.toggleExpand(newExpanded);
    if (fullscreen && !this.expanded) {
      this.epgCore.emitAnimationEnd();
    }
    this.setState({
      buttonExpanded: !buttonExpanded,
    });
  };

  switchProgram = (program, switchAfterClick) => {
    const { playingProgram } = this.props;
    if (program && program.channel.isLocked) {
      this.toggleExpand();
    } else if (playingProgram && playingProgram.startTimestamp === program.startTimestamp) {
      this.toggleExpand();
    } else {
      this.epgCore.switchProgram(program, switchAfterClick);
    }
  };

  isInteractive = () => {
    const { currentChannel } = this.props;
    return (
      _.get(currentChannel, 'type') === 'vodChannel' ||
      _.get(currentChannel, 'type') === 'dvrChannel'
    );
  };

  visibleFullScreenControls = (event) => {
    this.setState({
      visibleFullScreenControls: event.data,
    });
  };

  render() {
    const {
      programVisible,
      fullscreen,
      buttonExpanded,
      hovered,
      isCoreInited,
      visibleFullScreenControls,
    } = this.state;
    const {
      channelsMap, // map – channelId: channelObject
      activePrograms, // map – channelId: programObject
      channelsByGenres, // array - [{genre_id, genre_title, array_of_channels}]
      currentChannel,
      currentProgram,
      playingProgram,
      currentChannelProgramsMap,
      currentChannelProgramsList,
      currentChannelAbsoluteId,
      subscriptionInfoMap,
      isExtendedProgramList,
      showAllChannelsButton,
      allChannelsUrl,
    } = this.props;

    if (!isCoreInited) {
      return <Loader />;
    }

    return (
      <div onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
        <ProgramButton
          id="ProgramButton"
          onClick={this.toggleExpand}
          fullscreen={fullscreen}
          expanded={buttonExpanded}
          hovered={hovered}
          visibleFullScreenControls={visibleFullScreenControls}
        >
          <ChannelsButtonWrap>
            <ChannelsSideButton>
              <StyledIcon icon={ChannelsIcon} size={25} />
              <StyledText color="white" size="fs_l" font="gotham"
                type="uppercase">
                {this.epgCore.translate('channels', 'Каналы')}
              </StyledText>
            </ChannelsSideButton>
          </ChannelsButtonWrap>
          <ProgramsButtonWrap>
            <ProgramsSideButton>
              <StyledArrowIcon size={25}/>
              {/*<StyledIcon name="arrow_up" size={25} />*/}
              <StyledText color="white" size="fs_l" font="gotham"
                type="uppercase">
                {this.epgCore.translate('program', 'Программа')}
              </StyledText>
            </ProgramsSideButton>
          </ProgramsButtonWrap>
        </ProgramButton>
        <EpgStyled
          id="MainContainer"
          onTransitionEnd={this.onTransitionEnd}
          fullscreen={fullscreen}
          expanded={buttonExpanded}
        >
          <Sidebar
            channelsMap={channelsMap}
            activePrograms={activePrograms}
            channelsByGenres={channelsByGenres}
            switchChannel={this.epgCore.switchChannel}
            genreExpanded ={this.epgCore.expandGenre}
            currentChannel={currentChannel}
            currentChannelAbsoluteId={currentChannelAbsoluteId}
            translate={this.epgCore.translate}
            subscriptionInfoMap={subscriptionInfoMap}
            allChannelsUrl={allChannelsUrl}
            showAllChannelsButton={showAllChannelsButton}
          />
          {(this.expanded || this.programRendered) && (
            <DailyProgram
              isInteractive={this.isInteractive()}
              programsList={currentChannelProgramsList}
              programsMap={currentChannelProgramsMap}
              visible={programVisible}
              currentProgramTimestamp={_.get(currentProgram, 'startTimestamp')}
              playingProgramTimestamp={_.get(playingProgram, 'startTimestamp')}
              switchProgram={this.switchProgram}
              loadPrograms={this.epgCore.loadExtendedProgram}
              currentChannelId={_.get(currentChannel, 'id', null)}
              translate={this.epgCore.translate}
              isExtendedProgramList={isExtendedProgramList}
              toggleExpand={this.toggleExpand}
              onRestrictedUIClick={this.epgCore.onRestrictedUIClick}
              getMessageModeData={this.epgCore.getMessageModeData}
            />
          )}
        </EpgStyled>
      </div>
    );
  }
}

App.propTypes = {
  currentProgram: PropTypes.object,
  playingProgram: PropTypes.object,
  channelsMap: PropTypes.object,
  channelsByGenres: PropTypes.array,
  currentChannel: PropTypes.object,
  activePrograms: PropTypes.object,
  currentChannelAbsoluteId: PropTypes.object,
  currentChannelProgramsList: PropTypes.array,
  currentChannelProgramsMap: PropTypes.object,
  subscriptionInfoMap: PropTypes.object,
  isExtendedProgramList: PropTypes.bool,
  showAllChannelsButton: PropTypes.bool,
  allChannelsUrl: PropTypes.string,
};

App.defaultProps = {
  currentProgram: null,
  playingProgram: null,
  channelsByGenres: [],
  channelsMap: {},
  activePrograms: {},
  currentChannel: null,
  currentChannelAbsoluteId: null,
  currentChannelProgramsList: [],
  currentChannelProgramsMap: {},
  subscriptionInfoMap: {},
  isExtendedProgramList: false,
  showAllChannelsButton: false,
  allChannelsUrl: '',
};

const mapStateToProps = createStructuredSelector({
  activePrograms: activeProgramsSelector,
  currentProgram: currentProgramSelector,
  playingProgram: playingProgramSelector,
  channelsByGenres: channelsByGenresSelector,
  currentChannel: currentChannelSelector,
  channelsMap: channelsMapSelector,
  currentChannelProgramsMap: currentChannelProgramsMapSelector,
  currentChannelProgramsList: currentChannelProgramsListSelector,
  currentChannelAbsoluteId: currentChannelAbsoluteIdSelector,
  subscriptionInfoMap: subscriptionInfoMapSelector,
  isExtendedProgramList: isExtendedProgramListSelector,
  showAllChannelsButton: getShowAllChannelsButton,
  allChannelsUrl: getAllChannelsUrl,
});

const mapDispatchToProps = {
  ...epgActions
};

const ConnectedApp = connect(mapStateToProps, mapDispatchToProps)(App);

export default ConnectedApp;
